<script setup lang="ts">
import ContentEditorFooter from '../../ContentEditorFooter.vue'
import EditorTextField from '../../EditorTextField.vue'
import MissionIconField from './MissionIconField.vue'
import { ComboboxSelect } from '@/ui/combobox'
import { mirrorMissionFormalFrench, renderMissionText } from '../../missionText'
import useContentEditing from '../../../composables/useContentEditing'
import { useEditorLoyaltyData } from '../../../composables/editorLoyaltyData'
import { isValidHttpUrl } from '../../../lib/brandAppUrl'

import type { MissionInstance } from '../../../composables/missionInstances'
import type { WebappMission } from '@/types/types'

const { locale, t } = useI18n()
const router = useRouter()
const { user } = useLocalStorage()
const { pointsLabel } = useEditorLoyaltyData()
const { setDraft } = useContentEditing()

const props = defineProps<{
  mission: WebappMission
  missionKey: string
  instances: MissionInstance[]
  isLoadingInstances?: boolean
  instancesError?: boolean
}>()

const emit = defineEmits<{
  save: [mission: WebappMission]
  close: []
  refreshInstances: []
}>()

const MAX_POINTS = 100000
const MISSION_BUTTON_MAX_LENGTH = 30
const ALL_SURVEYS = 0

const draft = ref<WebappMission>(JSON.parse(JSON.stringify(props.mission)))

const hasInstance = computed(() => ['survey', 'contest_game'].includes(draft.value.type ?? ''))
const isSurvey = computed(() => draft.value.type === 'survey')
const isCustom = computed(() => draft.value.type === null)
const supportsPoints = computed(
  () => isCustom.value || ['survey', 'contest_game', 'review', 'crm'].includes(draft.value.type ?? '')
)
const buttonPlaceholder = computed(() =>
  t(`pages.loyaltySupports.webapp.editor.missionEditor.defaultButton.${draft.value.type ?? 'custom'}`)
)

const rendered = (field: 'title' | 'subtitle' | 'ctaLabel') =>
  renderMissionText(props.mission[field], locale.value, {
    points: props.mission.points,
    label: pointsLabel.value,
    merchantName: user.value.merchant.name ?? ''
  })

const text = (field: 'title' | 'subtitle' | 'ctaLabel') => {
  const value = ref(rendered(field))
  watch(value, (next) => {
    draft.value = { ...draft.value, [field]: { ...(draft.value[field] ?? {}), [locale.value]: next } }
  })

  return value
}

const title = text('title')
const subtitle = text('subtitle')
const ctaLabel = text('ctaLabel')

const normalizePoints = (value: string | number) => Math.min(MAX_POINTS, Math.max(0, Math.floor(Number(value) || 0)))

const pointsInput = ref(String(draft.value.points ?? 0))

const onPointsInput = (value: string | number) => {
  pointsInput.value = String(value ?? '')
  draft.value = { ...draft.value, points: normalizePoints(pointsInput.value) }
}

const commitPoints = () => {
  const points = normalizePoints(pointsInput.value)
  pointsInput.value = String(points)
  draft.value = { ...draft.value, points }
}

const destinationUrl = computed({
  get: () => draft.value.destinationUrl ?? '',
  set: (value: string) => {
    draft.value = { ...draft.value, destinationUrl: value }
  }
})

const instanceOptions = computed(() => {
  const options = props.instances.map((instance) => ({ value: instance.id, label: instance.label }))

  return isSurvey.value
    ? [{ value: ALL_SURVEYS, label: t('pages.loyaltySupports.webapp.missions.allSurveys') }, ...options]
    : options
})

const instanceModel = computed({
  get: () => (isSurvey.value && !draft.value.instanceId ? ALL_SURVEYS : (draft.value.instanceId ?? null)),
  set: (value: number | null) => {
    draft.value = { ...draft.value, instanceId: value === ALL_SURVEYS ? null : value }
  }
})

const createInstanceUrl = computed(
  () => router.resolve({ name: isSurvey.value ? 'SurveyCreate' : 'ContestCreate' }).href
)

const attempt = ref<'hide' | 'show' | null>(null)
const fieldErrors = computed<Partial<Record<'title' | 'url' | 'instance', string>>>(() => {
  if (!attempt.value) return {}
  const errors: Partial<Record<'title' | 'url' | 'instance', string>> = {}
  if (!title.value.trim()) errors.title = t('pages.loyaltySupports.webapp.editor.missionEditor.titleError')
  if (attempt.value === 'hide') return errors
  if (isCustom.value && !isValidHttpUrl(destinationUrl.value.trim()))
    errors.url = t('pages.loyaltySupports.webapp.editor.missionEditor.urlError')
  if (hasInstance.value && !isSurvey.value && !draft.value.instanceId)
    errors.instance = t('pages.loyaltySupports.webapp.missions.instanceRequired')

  return errors
})

const canSave = computed(() => !(hasInstance.value && props.isLoadingInstances))

watch(draft, (item) => setDraft({ kind: 'mission', key: props.missionKey, item: { ...item } }), {
  immediate: true,
  deep: true
})

const isVisible = ref(props.mission.isVisible)

const save = () => {
  commitPoints()
  attempt.value = isVisible.value ? 'show' : 'hide'
  if (!canSave.value || Object.keys(fieldErrors.value).length) return

  const next = { ...draft.value, isVisible: isVisible.value }
  ;(['title', 'subtitle', 'ctaLabel'] as const).forEach((field) => {
    const typed = { title, subtitle, ctaLabel }[field].value
    next[field] = typed === rendered(field) ? props.mission[field] : { ...next[field], [locale.value]: typed.trim() }
  })
  if (isCustom.value) next.destinationUrl = destinationUrl.value.trim()
  if (hasInstance.value) {
    next.instanceType = isSurvey.value ? (next.instanceId ? 'survey' : null) : 'contest_game'
  }
  emit('save', mirrorMissionFormalFrench(next, props.mission))
}
</script>

<template>
  <div class="flex flex-col">
    <form id="brand-app-mission-form" class="flex min-w-0 flex-col gap-5 px-6 pb-6" @submit.prevent>
      <div class="flex flex-col gap-2">
        <Label for="brand-app-mission-title">{{ t('pages.loyaltySupports.webapp.missions.title') }} *</Label>
        <Input
          id="brand-app-mission-title"
          v-model="title"
          :placeholder="t('pages.loyaltySupports.webapp.editor.missionEditor.titlePlaceholder')"
        />
        <p v-if="fieldErrors.title" class="text-destructive text-sm">{{ fieldErrors.title }}</p>
      </div>

      <div class="flex flex-col gap-2">
        <Label for="brand-app-mission-subtitle">{{ t('pages.loyaltySupports.webapp.missions.subtitle') }}</Label>
        <Input id="brand-app-mission-subtitle" v-model="subtitle" />
      </div>

      <MissionIconField v-model:icon-type="draft.iconType" v-model:icon-value="draft.iconValue" />

      <EditorTextField
        id="brand-app-mission-button"
        v-model="ctaLabel"
        :label="t('pages.loyaltySupports.webapp.editor.missionEditor.buttonLabel')"
        :max-length="MISSION_BUTTON_MAX_LENGTH"
        :placeholder="buttonPlaceholder"
        :default-value="buttonPlaceholder"
      />

      <div v-if="isCustom" class="flex flex-col gap-2">
        <Label for="brand-app-mission-url">{{ t('pages.loyaltySupports.webapp.editor.missionEditor.link') }} *</Label>
        <Input id="brand-app-mission-url" v-model="destinationUrl" type="url" placeholder="https://" />
        <p v-if="fieldErrors.url" class="text-destructive text-sm">{{ fieldErrors.url }}</p>
      </div>

      <template v-if="supportsPoints || hasInstance">
        <h3 class="border-border text-foreground border-t pt-5 text-sm font-semibold">
          {{ t('pages.loyaltySupports.webapp.editor.missionEditor.operation') }}
        </h3>

        <div v-if="supportsPoints" class="flex flex-col gap-2">
          <Label for="brand-app-mission-points">{{ t('pages.loyaltySupports.webapp.missions.points') }}</Label>
          <Input
            id="brand-app-mission-points"
            :model-value="pointsInput"
            type="number"
            min="0"
            :max="MAX_POINTS"
            step="1"
            @update:model-value="onPointsInput"
            @blur="commitPoints"
          />
        </div>

        <div v-if="hasInstance" class="flex flex-col gap-2">
          <Label for="brand-app-mission-instance">
            {{
              isSurvey
                ? t('pages.loyaltySupports.webapp.missions.survey')
                : t('pages.loyaltySupports.webapp.missions.contestGame')
            }}<span v-if="!isSurvey" class="text-destructive"> *</span>
          </Label>
          <p v-if="isLoadingInstances" class="text-muted-foreground text-sm">
            {{ t('pages.loyaltySupports.webapp.editor.missionEditor.loading') }}
          </p>
          <ComboboxSelect
            v-else-if="instanceOptions.length"
            id="brand-app-mission-instance"
            v-model="instanceModel"
            :options="instanceOptions"
            :placeholder="t('pages.loyaltySupports.webapp.missions.instancePlaceholder')"
            :search-threshold="6"
            trigger-class="w-full"
          />
          <p v-if="fieldErrors.instance" class="text-destructive text-sm">{{ fieldErrors.instance }}</p>

          <div
            v-if="!isLoadingInstances && !instances.length"
            class="border-input flex flex-col gap-1 rounded-md border border-dashed p-3"
          >
            <span v-if="instancesError" class="text-destructive text-sm">{{
              t('pages.loyaltySupports.webapp.missions.instancesLoadError')
            }}</span>
            <span v-else class="text-muted-foreground text-sm">
              {{
                isSurvey
                  ? t('pages.loyaltySupports.webapp.missions.noSurveyAvailable')
                  : t('pages.loyaltySupports.webapp.missions.noContestAvailable')
              }}
            </span>
            <div class="flex items-center gap-3">
              <Button variant="link" class="h-auto px-0" as-child>
                <a :href="createInstanceUrl" target="_blank" rel="noopener">
                  {{
                    isSurvey
                      ? t('pages.loyaltySupports.webapp.missions.createSurvey')
                      : t('pages.loyaltySupports.webapp.missions.createContest')
                  }}
                </a>
              </Button>
              <Button variant="link" class="text-muted-foreground h-auto px-0" @click="emit('refreshInstances')">
                {{ t('pages.loyaltySupports.webapp.missions.refreshInstances') }}
              </Button>
            </div>
          </div>
        </div>
      </template>
    </form>

    <ContentEditorFooter v-model:visible="isVisible" :disabled="!canSave" @cancel="emit('close')" @save="save" />
  </div>
</template>
